// Native in-app drop-off form — a faithful port of the real drop form
// (drop/worker.js). Same sections, copy, warnings, tooltips, per-roll notes,
// push/pull, live pricing summary, agreements, and Turbo FAQ. Backed by the
// drop worker's real endpoints via /api/drop/*. Email is known from the session
// (no email step). Club members skip delivery (auto gallery) + the club section.
// Note: memberships are currently closed, so the club section is waitlist-only
// (the embedded Stripe club checkout path is inactive upstream).
import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useRouter } from "../lib/router";
import { blankDropForm, type DropFormState } from "../lib/dropState";
import { SCAN_SIZES, FILE_FORMATS, SCAN_DELIVERY, PRINTS, NEGATIVES, PUSH_STOPS } from "../lib/dropOptions";
import { OrderSummary } from "./drop/OrderSummary";
import { FaqDog } from "./drop/FaqModal";
import { Card, Spinner } from "./ui";

interface SavedAddress {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}
interface DropContext {
  email: string;
  name: string;
  savedAddress: SavedAddress | null;
  exists: boolean;
  customerId: string | null;
  isClubMember: boolean;
  hasPreferences: boolean;
  preferences: Record<string, unknown>;
}

const ROLLS = [
  { label: "Color 35", count: "color35_count", notes: "color35_notes" },
  { label: "Color 120", count: "color120_count", notes: "color120_notes" },
  { label: "B+W 35", count: "bw35_count", notes: "bw35_notes" },
  { label: "B+W 120", count: "bw120_count", notes: "bw120_notes" },
] as const;

const num = (s: string) => parseInt(s || "0", 10) || 0;

function H1({ children }: { children: React.ReactNode }) {
  return <h1 className="text-[22px] font-bold text-navy text-center mb-3">{children}</h1>;
}
function Info({ children, tone = "warn" }: { children: React.ReactNode; tone?: "warn" | "grey" | "info" }) {
  const styles = {
    warn: "bg-[#fff3cd] border-[#ffc107] text-[#856404]",
    grey: "bg-[#f0f0f0] border-[#666] text-[#333]",
    info: "bg-[#f8f8fc] border-[#e0e0e8] text-ink-muted",
  }[tone];
  return <div className={`mt-3 p-3 rounded-lg border-2 text-[13px] leading-snug ${styles}`}>{children}</div>;
}

function Radio({
  checked,
  onSelect,
  children,
  disabled,
}: {
  checked: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 rounded-xl border flex items-center gap-3 disabled:opacity-40 ${
        checked ? "border-navy bg-navy-soft" : "border-line bg-surface"
      }`}
    >
      <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${checked ? "border-navy" : "border-line-strong"}`}>
        {checked && <span className="h-2.5 w-2.5 rounded-full bg-navy" />}
      </span>
      <span className="flex-1 text-ink text-[15px]">{children}</span>
    </button>
  );
}

export function DropForm({ formType = "drop-off" }: { formType?: "drop-off" | "mail-in" }) {
  const isMailIn = formType === "mail-in";
  const { navigate } = useRouter();
  const ctxQ = useQuery({
    queryKey: ["drop", "context", formType],
    queryFn: () => api.get<DropContext>(`/api/drop/context${isMailIn ? "?type=mail-in" : ""}`),
    staleTime: 5 * 60 * 1000,
  });
  const ctx = ctxQ.data;
  const club = !!ctx?.isClubMember;

  const [fd, setFd] = useState<DropFormState>(() => blankDropForm(""));
  const [stepIdx, setStepIdx] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [showPushPull, setShowPushPull] = useState(false);

  const set = <K extends keyof DropFormState>(k: K, v: DropFormState[K]) => setFd((f) => ({ ...f, [k]: v }));

  // sync email + prefill the name once context loads (authenticated customers
  // shouldn't have to type their name — we know who they are).
  const email = ctx?.email || "";
  if (email && fd.customer_email !== email) set("customer_email", email);
  const [namePrefilled, setNamePrefilled] = useState(false);
  if (ctx?.name && !fd.customer_name && !namePrefilled) {
    setNamePrefilled(true);
    set("customer_name", ctx.name);
  }
  const [addrPrefilled, setAddrPrefilled] = useState(false);
  if (ctx?.savedAddress && !fd.street1 && !addrPrefilled) {
    setAddrPrefilled(true);
    const a = ctx.savedAddress;
    setFd((f) => ({ ...f, street1: a.street1, street2: a.street2, city: a.city, state: a.state, zip: a.zip }));
  }

  const totalRolls = num(fd.color35_count) + num(fd.color120_count) + num(fd.bw35_count) + num(fd.bw120_count);
  const has35mm = num(fd.color35_count) + num(fd.bw35_count) > 0;
  const hasBW = num(fd.bw35_count) + num(fd.bw120_count) > 0;
  const scanNone = fd.scan_size === "none";
  // Real form warns on an exact 24/36 (classic frame counts — likely entered
  // the number of exposures, not rolls).
  const bigCount = [fd.color35_count, fd.color120_count, fd.bw35_count, fd.bw120_count].some((c) => num(c) === 24 || num(c) === 36);

  const steps = useMemo(() => {
    const s: { id: string; title: string }[] = [{ id: "name", title: "Welcome" }];
    if (ctx?.hasPreferences) s.push({ id: "prefs", title: "Your preferences" });
    s.push({ id: "film", title: "Your film" }, { id: "scan", title: "Scans" });
    if (!scanNone) {
      if (!club) s.push({ id: "delivery", title: "Delivery" });
      s.push({ id: "prints", title: "Prints" });
    }
    if (isMailIn) s.push({ id: "address", title: "Mailing address" });
    s.push({ id: "negatives", title: "Negatives" });
    if (isMailIn) s.push({ id: "prepaid", title: "Shipping" });
    if (!club) s.push({ id: "club", title: "Turbo's Club" });
    s.push({ id: "review", title: "Review" });
    return s;
  }, [scanNone, club, isMailIn, ctx?.hasPreferences]);

  const step = steps[Math.min(stepIdx, steps.length - 1)];

  function validate(): string | null {
    switch (step.id) {
      case "name":
        if (!/^[A-Za-z]+ +[A-Za-z]{2,}/.test(fd.customer_name.trim())) return "Please enter your first and last name.";
        return null;
      case "prefs":
        if (!fd.use_preferences) return "Please choose whether to use your saved preferences.";
        return null;
      case "film":
        if (totalRolls < 1) return "Please enter the number of rolls you're dropping off.";
        if (showPushPull) {
          if (fd.pushPull.some((r) => !r.filmType.trim() || !r.stops))
            return "Please complete each push/pull roll (film + stops), or uncheck push/pull.";
          if (fd.pushPull.length > totalRolls) return "You have more push/pull rolls than total rolls of film.";
        }
        return null;
      case "scan":
        if (!fd.scan_size) return "Please choose a scan size.";
        if (!scanNone && !fd.file_format) return "Please choose a file format.";
        return null;
      case "delivery":
        if (!fd.scan_delivery) return "Please choose a delivery method.";
        return null;
      case "prints":
        if (!fd.prints) return "Please make a prints selection.";
        return null;
      case "address":
        if (!fd.street1.trim() || !fd.city.trim() || !fd.state.trim() || !fd.zip.trim())
          return "Please provide your mailing address.";
        return null;
      case "negatives":
        if (!fd.negative_handling) return "Please choose what to do with your negatives.";
        if (!isMailIn && fd.negative_handling === "return" && (!fd.street1.trim() || !fd.city.trim() || !fd.state.trim() || !fd.zip.trim()))
          return "Please provide your mailing address.";
        return null;
      case "prepaid":
        // Real form doesn't require a prepaid choice — leave optional for parity.
        return null;
      case "club":
        if (!fd.join_club) return "Please make a selection.";
        return null;
      case "review":
        if (!fd.agree) return "Please check the agreement to submit.";
        return null;
      default:
        return null;
    }
  }

  function applySavedPrefs() {
    const p = (ctx?.preferences || {}) as Record<string, string>;
    setFd((f) => ({
      ...f,
      scan_size: p.scan_size || f.scan_size,
      file_format: p.file_format || f.file_format,
      prints: p.prints || f.prints,
      negative_handling: p.negative_handling || f.negative_handling,
      contact_sheets: p.contact_sheets === "yes" ? true : f.contact_sheets,
    }));
  }

  function next() {
    const err = validate();
    if (err) return setError(err);
    setError(null);
    // "Use saved preferences? yes" applies them and jumps straight to review
    // (matches the real form's skip-ahead). setStepIdx beyond bounds clamps to
    // the last step (always "review").
    if (step.id === "prefs" && fd.use_preferences === "yes") {
      applySavedPrefs();
      setStepIdx(steps.length);
      return;
    }
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else submit.mutate();
  }
  function back() {
    setError(null);
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  const submit = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        session_id: crypto.randomUUID(),
        customer_name: fd.customer_name.trim(),
        color35_count: fd.color35_count || "0",
        color120_count: fd.color120_count || "0",
        bw35_count: fd.bw35_count || "0",
        bw120_count: fd.bw120_count || "0",
        color35_notes: fd.color35_notes,
        color120_notes: fd.color120_notes,
        bw35_notes: fd.bw35_notes,
        bw120_notes: fd.bw120_notes,
        pushPullRolls: fd.pushPull.filter((r) => r.filmType.trim() && r.stops).map((r) => ({ filmType: r.filmType.trim(), stops: r.stops })),
        scan_size: fd.scan_size,
        // Develop-only wins over club (matches drop worker's if/else-if order):
        // a develop-only order has no scans to put in a gallery.
        scan_delivery: scanNone ? "" : club ? "gallery" : fd.scan_delivery,
        prints: scanNone ? "none" : fd.prints,
        negative_handling: fd.negative_handling,
        join_club: club ? "already" : fd.join_club || "no",
        additional_notes: fd.additional_notes,
        is_club_member: club,
        has_saved_preferences: !!ctx?.hasPreferences,
        _formType: formType, // tells the BFF whether to hit the ship host
      };
      if (!scanNone) {
        payload.file_format = fd.file_format;
        if (fd.contact_sheets) payload.contact_sheets = "yes";
      }
      if (fd.negative_handling === "pickup") payload.pickup_format = has35mm ? fd.pickup_format : "uncut";
      // Address is always collected for mail-in; drop-off only when mailing negatives back.
      if (isMailIn || fd.negative_handling === "return") {
        payload.street1 = fd.street1;
        payload.street2 = fd.street2;
        payload.city = fd.city;
        payload.state = fd.state;
        payload.zip = fd.zip;
      }
      if (isMailIn) payload.prepaid_label = fd.prepaid_label;
      return api.post<{ success: boolean; invoiceId: string }>("/api/drop/submit", payload);
    },
    onSuccess: (res) => {
      // Newsletter opt-in fires its own enrollment (matches the real form's
      // background /newsletter-signup call). Fire-and-forget.
      if (fd.newsletter) api.post("/api/drop/newsletter", { name: fd.customer_name.trim() }).catch(() => {});
      // Club members can save these selections as reusable preferences (writes
      // the same email-keyed KV the drop form reads).
      if (club && fd.save_preferences) {
        api
          .put(`/api/prefs?type=${isMailIn ? "mail" : "drop"}`, {
            scan_size: fd.scan_size,
            file_format: fd.file_format,
            prints: fd.prints,
            negative_handling: fd.negative_handling,
            contact_sheets: fd.contact_sheets ? "yes" : "",
          })
          .catch(() => {});
      }
      setDone(res.invoiceId);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Couldn't submit your order."),
  });

  // ── shell ─────────────────────────────────────────────────────────
  if (ctxQ.isLoading) {
    return (
      <div className="min-h-dvh grid place-items-center bg-canvas">
        <Spinner label="Getting things ready…" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-dvh bg-canvas flex flex-col items-center justify-center px-6 text-center gap-4">
        <img src="https://downloads.atxfilmlab.com/doggie_ver5.png" alt="" className="w-24 h-24 object-contain" />
        <h1 className="text-navy font-bold text-2xl">Order received!</h1>
        <p className="text-ink-muted text-[15px] max-w-sm">
          {isMailIn
            ? "Mail your film to us and we'll match it to this order. You can track turnaround, pay, and download your scans right here."
            : "Drop your film in the box at the lab and we'll match it to this order. You can track turnaround, pay, and download your scans right here."}
        </p>
        <button onClick={() => navigate("/")} className="h-12 px-6 rounded-xl bg-navy text-white font-medium">
          Go to my film
        </button>
      </div>
    );
  }

  const progress = ((stepIdx + 1) / steps.length) * 100;

  return (
    <div className="min-h-dvh bg-canvas flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-4 h-14 border-b border-line bg-surface sticky top-0 z-20">
        <img src="/brand/tpfl.png" alt="Turbo Photo Film Lab" className="h-7 w-auto" />
        <button onClick={() => navigate("/")} aria-label="Close" className="h-9 w-9 rounded-full text-ink-muted active:bg-line-soft">
          <i className="ph ph-x text-lg" />
        </button>
      </header>
      <div className="h-1 bg-line">
        <div className="h-full bg-navy transition-all" style={{ width: `${progress}%` }} />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-4 py-5 pb-40">
        <div className="max-w-md mx-auto">
          {/* NAME / WELCOME */}
          {step.id === "name" && (
            <div>
              <img src="/brand/tpfl.png" alt="Turbo Photo Film Lab" className="h-12 w-auto mx-auto mb-4" />
              <p className="text-center text-ink-muted text-[14px] leading-relaxed mb-5">
                We're so glad you're here! Our drop off process is quick and easy. If you have any questions throughout
                the process, just tap on <b>Turbo</b> (the dog on the skateboard) to pull up our FAQ!
              </p>
              <label className="text-[14px] font-medium text-ink">
                {ctx?.name ? "Confirm your name" : "Your name"}
              </label>
              <input
                autoFocus={!ctx?.name}
                value={fd.customer_name}
                onChange={(e) => set("customer_name", e.target.value)}
                placeholder="Full Name"
                className="mt-1 w-full h-12 px-4 rounded-xl border border-line bg-surface text-[16px] focus:border-navy outline-none"
              />
              <p className="text-[13px] text-ink-subtle mt-2">
                {ctx?.name ? "This is the name we have on file — tap Continue, or edit it." : `Signed in as ${email}`}
              </p>
            </div>
          )}

          {/* SAVED PREFERENCES (club members) */}
          {step.id === "prefs" && (
            <div>
              <H1>Use your saved preferences?</H1>
              <p className="text-[13px] text-ink-muted text-center mb-4">
                We saved your processing options from a previous order. Use them again and skip straight to review, or
                choose fresh for this order.
              </p>
              <div className="flex flex-col gap-2">
                <Radio checked={fd.use_preferences === "yes"} onSelect={() => set("use_preferences", "yes")}>
                  Yes, use my saved preferences
                </Radio>
                <Radio checked={fd.use_preferences === "no"} onSelect={() => set("use_preferences", "no")}>
                  No, I'll choose for this order
                </Radio>
              </div>
            </div>
          )}

          {/* FILM */}
          {step.id === "film" && (
            <div>
              <H1>How many rolls are you {isMailIn ? "mailing in" : "dropping off"}?</H1>
              <div className="flex flex-col gap-3">
                {ROLLS.map((r) => (
                  <div key={r.count} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-3">
                      <label className="w-20 text-ink font-medium text-[15px]">{r.label}</label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={fd[r.count as keyof DropFormState] as string}
                        onChange={(e) => set(r.count as keyof DropFormState, e.target.value.replace(/\D/g, "") as never)}
                        placeholder="0"
                        className="w-20 h-11 px-3 text-center rounded-lg border border-line bg-surface text-[16px]"
                      />
                      <input
                        type="text"
                        value={fd[r.notes as keyof DropFormState] as string}
                        onChange={(e) => set(r.notes as keyof DropFormState, e.target.value as never)}
                        placeholder="Notes about rolls"
                        className="flex-1 h-11 px-3 rounded-lg border border-line bg-surface text-[14px]"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {bigCount && (
                <Info tone="warn">
                  <i className="ph ph-warning" /> Are you sure? Input the number of <i>rolls</i> of film you're dropping
                  off, not the number on the roll of film itself. If you're dropping off that much film, please ignore!
                </Info>
              )}

              {/* Push / pull */}
              <div className="mt-4 border-t border-line-soft pt-4">
                <label className="flex items-center gap-3 text-[15px] text-ink">
                  <input
                    type="checkbox"
                    checked={showPushPull}
                    onChange={(e) => {
                      setShowPushPull(e.target.checked);
                      if (e.target.checked && fd.pushPull.length === 0) set("pushPull", [{ filmType: "", stops: "" }]);
                      if (!e.target.checked) set("pushPull", []);
                    }}
                    className="h-5 w-5 accent-navy"
                  />
                  I have film that needs push/pull processing
                </label>
                {showPushPull && (
                  <div className="mt-3">
                    {fd.pushPull.map((row, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2">
                        <input
                          value={row.filmType}
                          onChange={(e) => {
                            const pp = [...fd.pushPull];
                            pp[i] = { ...pp[i], filmType: e.target.value };
                            set("pushPull", pp);
                          }}
                          placeholder="Film (e.g. Portra 400)"
                          className="flex-1 h-10 px-3 rounded-lg border border-line bg-surface text-[14px]"
                        />
                        <select
                          value={row.stops}
                          onChange={(e) => {
                            const pp = [...fd.pushPull];
                            pp[i] = { ...pp[i], stops: e.target.value };
                            set("pushPull", pp);
                          }}
                          className="h-10 px-2 rounded-lg border border-line bg-surface text-[14px]"
                        >
                          <option value="">Stops</option>
                          {PUSH_STOPS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={() => set("pushPull", fd.pushPull.filter((_, j) => j !== i))} className="h-10 w-9 text-ink-subtle" aria-label="Remove">
                          <i className="ph ph-x" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => set("pushPull", [...fd.pushPull, { filmType: "", stops: "" }])} className="text-[13px] text-navy font-medium">
                      <i className="ph ph-plus" /> Add Another Roll
                    </button>
                    <Info tone="grey">
                      Pushing and pulling film means changing exposure by changing development time; pushing is more,
                      pulling is less, and the values are the 'stop' of exposure change. +1 makes 200 ISO into 400 ISO,
                      −2 makes 800 into 200, etc. Only use this if the film was intentionally shot for push processing.{" "}
                      <b>$2 per push/pull roll.</b> Please mark your canisters with a sharpie so they're clearly
                      differentiated!
                    </Info>
                  </div>
                )}
              </div>

              {hasBW && (
                <Info tone="grey">
                  <i className="ph ph-alarm" /> Orders that include black and white will have a two week turnaround for
                  all rolls.
                </Info>
              )}
            </div>
          )}

          {/* SCAN */}
          {step.id === "scan" && (
            <div>
              <H1>How would you like your digital scans?</H1>
              <div className="rounded-xl border-2 border-[#e0e0e8] bg-[#f8f8fc] p-4 mb-4 text-[13px] leading-relaxed">
                <p className="font-semibold text-navy text-center mb-2">Scan size recommendations:</p>
                <p className="mb-1">
                  <b className="text-navy">Small</b> — &lt;1mb — small but high quality and space saving!
                </p>
                <p className="mb-1">
                  <b className="text-navy">High Res</b> — 1–5mb — perfect for Instagram, small prints — recommended for
                  disposables
                </p>
                <p>
                  <b className="text-navy">Max Res</b> — 12–25mb — professional work, larger prints, and archival
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {SCAN_SIZES.map((s) => (
                  <Radio key={s.value} checked={fd.scan_size === s.value} onSelect={() => set("scan_size", s.value)}>
                    {s.value === "small" ? "Small" : s.value === "high_res" ? "High Res" : s.value === "max_res" ? "Max Res (+$3 / roll)" : "No Digitals (Develop Only)"}
                  </Radio>
                ))}
              </div>
              {scanNone && (
                <Info tone="warn">
                  <i className="ph ph-warning" /> This option means you will be scanning your film yourself. If so,
                  that's awesome! If not, please select a scan resolution and file format.
                </Info>
              )}
              {!scanNone && (
                <>
                  <h2 className="text-[17px] font-semibold text-ink mt-5 mb-2 text-center">What file format would you like?</h2>
                  <div className="flex flex-col gap-2">
                    {FILE_FORMATS.map((f) => (
                      <Radio key={f.value} checked={fd.file_format === f.value} onSelect={() => set("file_format", f.value)}>
                        {f.value === "jpg" ? "JPG" : "TIFF (+$2 / roll)"}
                      </Radio>
                    ))}
                  </div>
                  <h2 className="text-[17px] font-semibold text-ink mt-5 mb-1 text-center">Would you like digital contact sheets?</h2>
                  <p className="text-[13px] text-ink-muted text-center mb-2">
                    A contact sheet is all the scans from your roll in one image for easy viewing and sharing.{" "}
                    <a href="https://downloads.atxfilmlab.com/contact_sheet.png" target="_blank" rel="noopener noreferrer" className="text-orange font-medium">
                      They look like this!
                    </a>{" "}
                    Leave blank if not desired.
                  </p>
                  <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer ${fd.contact_sheets ? "border-navy bg-navy-soft" : "border-orange bg-surface"}`}>
                    <input type="checkbox" checked={fd.contact_sheets} onChange={(e) => set("contact_sheets", e.target.checked)} className="h-5 w-5 accent-navy" />
                    <span className="text-ink text-[15px]">Yes, I would like digital contact sheets (+$2 / roll)</span>
                  </label>
                </>
              )}
            </div>
          )}

          {/* DELIVERY */}
          {step.id === "delivery" && (
            <div>
              <H1>How would you like your scans delivered?</H1>
              <p className="text-[13px] text-ink-muted text-center mb-4 leading-relaxed">
                Check out an example of our custom online gallery{" "}
                <a href="https://demo.club.atxfilmlab.com/customers/turbo_butterball" target="_blank" rel="noopener noreferrer" className="text-orange">
                  here
                </a>
                ! Each file and roll can be downloaded individually and they stay in the cloud forever. <em>Turbo's Club</em>{" "}
                members automatically get a single gallery for all their orders.
              </p>
              <div className="flex flex-col gap-2">
                {SCAN_DELIVERY.map((d) => (
                  <Radio key={d.value} checked={fd.scan_delivery === d.value} onSelect={() => set("scan_delivery", d.value)}>
                    {d.value === "email" ? "A zip file via email is fine!" : "I would like an online gallery for viewing, sharing, and downloading my scans (+$6 / roll)"}
                  </Radio>
                ))}
              </div>
            </div>
          )}

          {/* PRINTS */}
          {step.id === "prints" && (
            <div>
              <H1>Would you like physical 4x6 prints?</H1>
              <p className="text-[13px] text-ink-muted text-center mb-4">
                Prints are $.46 each and right now we only print whole rolls. It usually comes out to around $14 per roll.
              </p>
              <div className="flex flex-col gap-2">
                {PRINTS.map((p) => (
                  <Radio key={p.value} checked={fd.prints === p.value} onSelect={() => set("prints", p.value)}>
                    {p.value === "none" ? "No thanks!" : p.value === "all" ? "Yes, for all my rolls" : "Yes, I'll tell you for which rolls in the notes"}
                  </Radio>
                ))}
              </div>
            </div>
          )}

          {/* ADDRESS (mail-in) */}
          {step.id === "address" && (
            <div>
              <H1>What's your mailing address?</H1>
              <div className="flex flex-col gap-2">
                <input value={fd.street1} onChange={(e) => set("street1", e.target.value)} placeholder="Street Address" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                <input value={fd.street2} onChange={(e) => set("street2", e.target.value)} placeholder="Apt / Suite / Unit (optional)" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                <input value={fd.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                <div className="flex gap-2">
                  <input value={fd.state} onChange={(e) => set("state", e.target.value)} placeholder="State" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px] w-24" />
                  <input value={fd.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="ZIP Code" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px] flex-1" />
                </div>
              </div>
            </div>
          )}

          {/* PREPAID (mail-in) */}
          {step.id === "prepaid" && (
            <div>
              <H1>Would you like a prepaid shipping label?</H1>
              <p className="text-[13px] text-ink-muted text-center mb-4">
                We can email you a prepaid shipping label to send us your film and add the cost to your invoice.
                {club ? " As a club member, this is free!" : ""}
              </p>
              <div className="flex flex-col gap-2">
                <Radio checked={fd.prepaid_label === "yes"} onSelect={() => set("prepaid_label", "yes")}>
                  Yes, please send me a prepaid label
                </Radio>
                <Radio checked={fd.prepaid_label === "no"} onSelect={() => set("prepaid_label", "no")}>
                  No thanks, I'll handle shipping myself
                </Radio>
              </div>
            </div>
          )}

          {/* NEGATIVES */}
          {step.id === "negatives" && (
            <div>
              <H1>Do you want your negatives (processed film) back?</H1>
              <p className="text-[13px] text-ink-muted mb-4 leading-relaxed">
                About 25% of people opt to keep their negatives; this is mostly done by people who archive or self-scan
                all of their film. Having your negatives is not required to get physical prints of your photos! Prints can
                be made directly from the scanned images you'll receive from us. If you don't have a need to keep your
                negatives, we'll safely dispose of them for you.
              </p>
              <div className="flex flex-col gap-2">
                <Radio checked={fd.negative_handling === "dispose"} onSelect={() => set("negative_handling", "dispose")} disabled={scanNone}>
                  Please safely dispose of them
                </Radio>
                <Radio checked={fd.negative_handling === "pickup"} onSelect={() => set("negative_handling", "pickup")}>
                  I'll schedule a time to pick them up
                </Radio>
                <Radio checked={fd.negative_handling === "return"} onSelect={() => set("negative_handling", "return")}>
                  Please mail them back to me cut and sleeved (+${isMailIn ? "8" : "6"} / order)
                </Radio>
              </div>

              {scanNone && (
                <Info tone="warn">
                  <i className="ph ph-warning" /> You said you'll be scanning your film yourself, so if we discarded it,
                  you'd get no film back. Choose pickup or mail-back.
                </Info>
              )}

              {fd.negative_handling === "pickup" && has35mm && (
                <div className="mt-4">
                  <p className="text-[14px] text-ink font-medium mb-2">I'd like my picked up negatives…</p>
                  <div className="flex flex-col gap-2">
                    <Radio checked={fd.pickup_format === "cut"} onSelect={() => set("pickup_format", "cut")}>
                      Cut and sleeved (35mm only)
                    </Radio>
                    <Radio checked={fd.pickup_format === "uncut"} onSelect={() => set("pickup_format", "uncut")}>
                      Uncut
                    </Radio>
                  </div>
                </div>
              )}

              {!isMailIn && fd.negative_handling === "return" && (
                <div className="mt-4">
                  <p className="text-[14px] text-ink font-medium mb-2">Please provide your mailing address:</p>
                  <div className="flex flex-col gap-2">
                    <input value={fd.street1} onChange={(e) => set("street1", e.target.value)} placeholder="Street Address" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                    <input value={fd.street2} onChange={(e) => set("street2", e.target.value)} placeholder="Apt / Suite / Unit (optional)" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                    <input value={fd.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px]" />
                    <div className="flex gap-2">
                      <input value={fd.state} onChange={(e) => set("state", e.target.value)} placeholder="State" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px] w-24" />
                      <input value={fd.zip} onChange={(e) => set("zip", e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="ZIP Code" className="h-11 px-3 rounded-xl border border-line bg-surface text-[16px] flex-1" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CLUB (waitlist) */}
          {step.id === "club" && (
            <div>
              <H1>Would you like to join Turbo's Club?</H1>
              <p className="text-[13px] text-ink-muted mb-3 leading-relaxed">
                Turbo's Club is TPFL's membership program. You get priority turnaround (including same day before 3pm!),
                a permanent gallery for all your scans, every 10th roll free, access to exclusive merch, free prepaid
                shipping labels to mail in film, and more! $10/mo, cancel anytime.
              </p>
              <div className="rounded-lg border-2 border-[#ffc107] bg-[#fff3cd] text-[#856404] p-3 text-[13px] mb-3 leading-snug">
                Memberships are currently at capacity! Join the waitlist below and we'll let you know as soon as a spot
                opens up.
              </div>
              <div className="flex flex-col gap-2">
                <Radio checked={fd.join_club === "waitlist"} onSelect={() => set("join_club", "waitlist")}>
                  Add me to the waitlist
                </Radio>
                <Radio checked={fd.join_club === "no"} onSelect={() => set("join_club", "no")}>
                  Maybe another time
                </Radio>
              </div>
            </div>
          )}

          {/* REVIEW */}
          {step.id === "review" && (
            <div>
              <H1>Anything else we should know?</H1>
              <textarea
                value={fd.additional_notes}
                onChange={(e) => set("additional_notes", e.target.value)}
                rows={4}
                placeholder="These are test rolls, new camera, went through airport scanners, I want prints on the disposable, etc!"
                className="w-full px-3 py-2 rounded-xl border border-line bg-surface text-[15px]"
              />

              <h2 className="text-[20px] font-bold text-navy mt-5 text-center">Order Summary</h2>
              <OrderSummary fd={{ ...fd, customer_email: email }} isClubMember={club} isMailIn={isMailIn} />

              {club && (
                <label className="mt-4 flex items-center gap-3 p-3 rounded-xl border-2 border-navy-line bg-navy-soft/40">
                  <input
                    type="checkbox"
                    checked={fd.save_preferences}
                    onChange={(e) => set("save_preferences", e.target.checked)}
                    className="h-5 w-5 accent-navy shrink-0"
                  />
                  <span className="text-ink text-[14px]">Save my processing preferences for future orders</span>
                </label>
              )}

              <label className="mt-4 flex items-start gap-3 p-3 rounded-xl border-2 border-line bg-surface">
                <input type="checkbox" checked={fd.agree} onChange={(e) => set("agree", e.target.checked)} className="h-5 w-5 accent-navy mt-0.5 shrink-0" />
                <span className="text-ink text-[13px] leading-snug">
                  I understand that I'm dropping off film at (or mailing to) a lab that is a <b>very</b> small,
                  family-run operation. I am assuming the responsibility of reading the FAQ and all the dropbox materials
                  to answer my own questions, and I know that if something is wrong, the team will reach out to me, and if
                  not, I'll get my film as soon as it's done.
                </span>
              </label>
              <label className="mt-3 flex items-center gap-3 p-3 rounded-xl border-2 border-navy bg-[#f8f8fc]">
                <input type="checkbox" checked={fd.newsletter} onChange={(e) => set("newsletter", e.target.checked)} className="h-5 w-5 accent-navy shrink-0" />
                <span className="text-ink text-[14px]">Sign up for our monthly newsletter</span>
              </label>
            </div>
          )}

          {error && <p className="text-sev-error text-sm mt-4">{error}</p>}

          {/* Turbo — in the content flow so it never overlaps the nav/controls */}
          <FaqDog />
        </div>
      </div>

      {/* Sticky footer nav */}
      <div className="fixed bottom-0 inset-x-0 z-20 bg-surface border-t border-line px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
        <div className="max-w-md mx-auto flex gap-2">
          {stepIdx > 0 && (
            <button onClick={back} className="h-12 px-5 rounded-xl bg-navy-soft text-navy font-medium border border-navy-line">
              Back
            </button>
          )}
          <button
            onClick={next}
            disabled={submit.isPending || (step.id === "review" && !fd.agree)}
            className="flex-1 h-12 rounded-xl bg-navy text-white font-medium disabled:opacity-50"
          >
            {step.id === "review" ? (submit.isPending ? "Submitting…" : "Submit Order") : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
